// src/pages/Consumables.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

export default function Consumables() {
  const { projectId } = useParams();

  // ─── State ─────────────────────────────────────────────────
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [manualConsumables, setManualConsumables] = useState([]);
  const [taxPct, setTaxPct] = useState(0);
  const [rmInputs, setRmInputs] = useState([]); // rmConsumptionInputs
  const [capacityUtil, setCapacityUtil] = useState([]); // capacityUtilizationPerYear
  const [projectYears, setProjectYears] = useState([]);
  const [termLoanInput, setTermLoanInput] = useState({ startMonth: "" });
  const [salesInputs, setSalesInputs] = useState([]); // annualSalesRealisationInputs
  const [manufacturedProducts, setManufacturedProducts] = useState([]); // manufacturedProducts

  // ─── Fetch from API ─────────────────────────────────────────
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => {
        setManualConsumables(data.manualConsumables || []);
        setTaxPct(parseFloat(data.consumablesExpensePct) || 0);

        setRmInputs(data.rmConsumptionInputs || []);
        setCapacityUtil(data.capacityUtilizationPerYear || []);
        setProjectYears(data.projectYears || []);
        setTermLoanInput(data.termLoanInput || { startMonth: "" });

        setSalesInputs(data.annualSalesRealisationInputs || []);
        setManufacturedProducts(data.manufacturedProducts || []);
      })
      .catch(() => setError("Failed to load consumables data."))
      .finally(() => setLoading(false));
  }, [projectId]);

  // ─── “mm-yyyy” → { month, year } ────────────────────────────
  const prod = useMemo(() => {
    const [m, y] = (termLoanInput.startMonth || "").split("-").map(Number);
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  // ─── Months active in a FY ─────────────────────────────────
  const getMonthsActive = useCallback(
    (fyLabel) => {
      if (!prod) return 12;
      const fyStart = parseInt(fyLabel.slice(0, 4), 10);
      if (isNaN(fyStart)) return 12;
      const fyEnd = fyStart + 1;
      const raw = (fyEnd - prod.year) * 12 + (3 - prod.month + 1);
      return Math.max(0, Math.min(raw, 12));
    },
    [prod]
  );

  // ─── Installed cap per product (MT/year) ───────────────────
  const installedByProduct = useMemo(() => {
    return rmInputs.reduce((map, r) => {
      const d = parseFloat(r.workingDays) || 0;
      const h = parseFloat(r.workingHoursPerDay) || 0;
      const o = parseFloat(r.outputPerHour) || 0;
      const inst = (d * h * o) / 1000;
      map[r.products] = (map[r.products] || 0) + inst;
      return map;
    }, {});
  }, [rmInputs]);

  // ─── Production MT per product per FY ──────────────────────
  const productionByProduct = useMemo(() => {
    const out = {};
    projectYears.forEach((fy, yi) => {
      const util = parseFloat(capacityUtil[yi]) || 0;
      const months = getMonthsActive(fy);
      manufacturedProducts.forEach(({ product }) => {
        const inst = installedByProduct[product] || 0;
        const mt = inst * (util / 100) * (months / 12);
        out[product] = out[product] || [];
        out[product][yi] = mt;
      });
    });
    return out;
  }, [
    projectYears,
    capacityUtil,
    manufacturedProducts,
    installedByProduct,
    getMonthsActive,
  ]);

  // ─── Avg working days & inventory days & price map ─────────
  const avgWorkingDays = useMemo(() => {
    if (!rmInputs.length) return 0;
    return (
      rmInputs.reduce((sum, r) => sum + (parseFloat(r.workingDays) || 0), 0) /
      rmInputs.length
    );
  }, [rmInputs]);

  const avgInventoryDays = useMemo(() => {
    if (!salesInputs.length) return 0;
    return (
      salesInputs.reduce(
        (sum, s) => sum + (parseFloat(s.inventoryDays) || 0),
        0
      ) / salesInputs.length
    );
  }, [salesInputs]);

  const avgPriceMap = useMemo(() => {
    return salesInputs.reduce((m, s) => {
      m[s.product] = parseFloat(s.avgSalePrice) || 0;
      return m;
    }, {});
  }, [salesInputs]);

  // ─── Build per-product Annual Sales Realisation ─────────────
  const totalAnnualSales = useMemo(() => {
    const perProductSales = manufacturedProducts.reduce((acc, { product }) => {
      const yearlyMT = productionByProduct[product] || [];
      const closing = yearlyMT.map((mt) =>
        avgWorkingDays ? (mt / avgWorkingDays) * avgInventoryDays : 0
      );
      const opening = closing.map((_, i) => (i === 0 ? 0 : closing[i - 1]));
      const netSell = yearlyMT.map((mt, i) => opening[i] + mt - closing[i]);
      const price = avgPriceMap[product] || 0;
      acc[product] = netSell.map((ns) => (ns * price) / 100000);
      return acc;
    }, {});

    return projectYears.map((_, yi) =>
      manufacturedProducts.reduce(
        (sum, { product }) => sum + (perProductSales[product]?.[yi] || 0),
        0
      )
    );
  }, [
    manufacturedProducts,
    productionByProduct,
    projectYears,
    avgWorkingDays,
    avgInventoryDays,
    avgPriceMap,
  ]);

  // ─── First non-zero production index ───────────────────────
  const firstNonZeroIdx = useMemo(() => {
    return projectYears.findIndex((_, yi) => {
      const tot = manufacturedProducts.reduce(
        (s, { product }) => s + (productionByProduct[product]?.[yi] || 0),
        0
      );
      return tot > 0;
    });
  }, [projectYears, manufacturedProducts, productionByProduct]);

  const basisIdx = firstNonZeroIdx < 0 ? 0 : firstNonZeroIdx;
  const salesForBasis = totalAnnualSales[basisIdx] || 0;

  // ─── Installed array, annual production & productionByYear ──
  const installedArr = useMemo(
    () =>
      Object.entries(installedByProduct).map(([product, inst]) => ({
        product,
        installed: inst,
      })),
    [installedByProduct]
  );

  const totalAnnualProduction = useMemo(
    () => installedArr.reduce((s, r) => s + r.installed, 0),
    [installedArr]
  );

  const productionByYear = useMemo(
    () =>
      projectYears.map((_, yi) => {
        const util = parseFloat(capacityUtil[yi]) || 0;
        const months = getMonthsActive(projectYears[yi]);
        return installedArr
          .map((r) => (r.installed * util * months) / (100 * 12))
          .reduce((s, v) => s + v, 0);
      }),
    [projectYears, capacityUtil, installedArr, getMonthsActive]
  );

  // ─── Consumables calculations ──────────────────────────────
  const amountFirstRow = useMemo(
    () => +(salesForBasis * (taxPct / 100)).toFixed(3),
    [salesForBasis, taxPct]
  );

  const manualRows = useMemo(
    () =>
      manualConsumables.map((r) => {
        const pct = parseFloat(r.taxPercent) || 0;
        const amt = +(salesForBasis * (pct / 100)).toFixed(3);
        return {
          slno: r.slno,
          particular: r.particular,
          annualSales: salesForBasis.toFixed(3),
          taxPct: pct.toFixed(3),
          amount: amt.toFixed(3),
        };
      }),
    [manualConsumables, salesForBasis]
  );

  const productTotal = amountFirstRow;
  const manualTotal = manualRows.reduce((s, r) => s + parseFloat(r.amount), 0);
  const grandTotal = (productTotal + manualTotal) * 100000;
  const costPerMT = totalAnnualProduction
    ? grandTotal / totalAnnualProduction
    : 0;

  const consumablesCostList = useMemo(
    () =>
      projectYears.map((_, i) => {
        const prodVal = productionByYear[i] || 0;
        return +((prodVal * costPerMT) / 100000).toFixed(3);
      }),
    [projectYears, productionByYear, costPerMT]
  );

  // ─── Render ────────────────────────────────────────────────
  const fmt = (n) =>
    typeof n === "number"
      ? n.toLocaleString("en-IN", { maximumFractionDigits: 2 })
      : "";

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <svg
          className="animate-spin h-10 w-10 text-gray-500"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8v8H4z"
          />
        </svg>
      </div>
    );
  }
  if (error) {
    return <div className="text-red-600 text-center py-8">{error}</div>;
  }

  return (
    <div className="bg-white">
      {/* From Product List */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">From Product List</h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium">
                  Sl. No.
                </th>
                <th className="px-2 py-1 border border-gray-200 text-left font-medium">
                  Particular
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Annual Sales (lacs)
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Tax %
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Amount (lacs)
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-2 py-1 border border-gray-200">1</td>
                <td className="px-2 py-1 border border-gray-200">
                  Percentage of expense on cartons, polybags, ropes etc
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {fmt(salesForBasis)}
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {fmt(taxPct)}%
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {fmt(amountFirstRow)}
                </td>
              </tr>
              <tr className="bg-gray-200 font-bold">
                <td
                  colSpan={4}
                  className="px-2 py-1 border border-gray-200 text-right"
                >
                  Total Amount (Product List)
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {fmt(productTotal)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Manual Consumables */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Manual Consumables</h2>
        {manualRows.length > 0 ? (
          <div className="overflow-x-auto shadow-lg rounded-lg">
            <table className="min-w-full table-auto border-collapse">
              <thead className="bg-gray-100">
                <tr className="h-8">
                  <th className="px-2 py-1 border border-gray-200 text-left font-medium">
                    Sl. No.
                  </th>
                  <th className="px-2 py-1 border border-gray-200 text-left font-medium">
                    Particular
                  </th>
                  <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                    Annual Sales (lacs)
                  </th>
                  <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                    Tax %
                  </th>
                  <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                    Amount (lacs)
                  </th>
                </tr>
              </thead>
              <tbody>
                {manualRows.map((r, i) => (
                  <tr key={i} className="even:bg-gray-50">
                    <td className="px-2 py-1 border border-gray-200">
                      {r.slno}
                    </td>
                    <td className="px-2 py-1 border border-gray-200">
                      {r.particular}
                    </td>
                    <td className="px-2 py-1 border border-gray-200 text-right">
                      {r.annualSales}
                    </td>
                    <td className="px-2 py-1 border border-gray-200 text-right">
                      {r.taxPct}%
                    </td>
                    <td className="px-2 py-1 border border-gray-200 text-right">
                      {r.amount}
                    </td>
                  </tr>
                ))}
                <tr className="bg-gray-200 font-bold">
                  <td
                    colSpan={4}
                    className="px-2 py-1 border border-gray-200 text-right"
                  >
                    Total Amount (Manual List)
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-right">
                    {fmt(manualTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center text-gray-600 py-4">
            No manual consumables available.
          </div>
        )}
      </section>

      {/* Summary */}
      <section className="mb-8">
        <h2 className="text-xl font-semibold mb-4">Summary</h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <tbody>
              <tr className="even:bg-gray-50">
                <td className="px-2 py-1 border border-gray-200 font-medium">
                  Grand Total (₹)
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {fmt(grandTotal)}
                </td>
              </tr>
              <tr className="even:bg-gray-50">
                <td className="px-2 py-1 border border-gray-200 font-medium">
                  Total Annual Production (MT)
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {fmt(totalAnnualProduction)}
                </td>
              </tr>
              <tr className="bg-gray-200 font-bold">
                <td className="px-2 py-1 border border-gray-200 font-medium">
                  Cost per MT (₹)
                </td>
                <td className="px-2 py-1 border border-gray-200 text-right">
                  {fmt(costPerMT)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* Year-wise Consumables Cost */}
      <section>
        <h2 className="text-xl font-semibold mb-4">
          Year-wise Consumables Cost
        </h2>
        <div className="overflow-x-auto shadow-lg rounded-lg">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-gray-100">
              <tr className="h-8">
                <th className="px-2 py-1 border border-gray-200 text-left font-medium">
                  Year
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Production (MT)
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Cost/MT (₹)
                </th>
                <th className="px-2 py-1 border border-gray-200 text-right font-medium">
                  Total Cost (lacs)
                </th>
              </tr>
            </thead>
            <tbody>
              {projectYears.map((yr, i) => (
                <tr key={i} className="even:bg-gray-50">
                  <td className="px-2 py-1 border border-gray-200">{yr}</td>
                  <td className="px-2 py-1 border border-gray-200 text-right">
                    {fmt(productionByYear[i])}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-right">
                    {fmt(costPerMT)}
                  </td>
                  <td className="px-2 py-1 border border-gray-200 text-right">
                    {fmt(consumablesCostList[i])}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
